import { useEffect, useState, useRef, useMemo } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { docMgmtI18n } from './i18n';
import CardStats from '../../components/CardStats/CardStats';
import ModalNewSending from './partials/ModalNewSending/ModalNewSending';
import ModalSendingDetails from './partials/ModalSendingDetails/ModalSendingDetails';
import ModalCancelRequest from './partials/ModalCancelRequest/ModalCancelRequest';
import {
  getDocumentSendPaged,
  getDocumentSendStats,
} from '../../services/documentManagementServices';
import { formatDate } from '../../utils/formatDate';
import './documentManagement.css';
import { getRelatedData } from '../../services/documentConfigService';
import { getCurrentUser } from '../../services/authService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { excelExportLogService } from 'services/excelLogFromFront';
import usePermissionsSmartDocuments from 'permissions/modules/smartDocuments/usePermissionsSmartDocuments';

const statusIdTable = 1;

const DocumentManagement = ({ DesignSystemComponents }) => {
  const { Input, Button, Table, IconButton, Card } = DesignSystemComponents;

  const { t, language: dmLanguage } = useScopedTranslation(docMgmtI18n);

  const SORT_HUMAN_LABELS = useMemo(
    () => ({
      name: t('dm_sortName'),
      documentType: t('dm_sortDocType'),
      caseIdentifier: t('dm_sortCase'),
      solicitorName: t('dm_sortUser'),
      createdAt: t('dm_sortDate'),
    }),
    [dmLanguage]
  );

  const [userId, setUserId] = useState(null);

  const [showModalNewSending, setShowModalNewSending] = useState(false);
  const [showModalSendingDetails, setShowModalSendingDetails] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [showModalCancelRequest, setShowModalCancelRequest] = useState(false);

  const [loading, setLoading] = useState(false);

  const [tableLoading, setTableLoading] = useState(false);

  const [totalPendingStat, setTotalPendingStat] = useState('');
  const [totalPending, setTotalPending] = useState([]);

  const [pageSize, setPageSize] = useState(10);
  const [documentManagementTablePage, setDocumentManagementTablePage] =
    useState(0);

  const [totalCount, setTotalCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const debounceRef = useRef(null);

  const [serviceOptions, setServiceOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);

  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const {
    canCreateSubmission,
    canDownloadManagementExcel,
    canViewDocument,
    canCancelRequest,
  } = usePermissionsSmartDocuments();

  const handleSearchChange = (text) => {
    setSearchDraft(text);
    setTableLoading(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDocumentManagementTablePage(0);
      setSearchTerm(text);
    }, 350);
  };

  const fetchDocumentSendStatsFn = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const result = await getDocumentSendStats.getStats(userId);
      setTotalPendingStat(result.totalPending);
    } catch (error) {
      console.warn('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentSendPagedFn = async () => {
    try {
      setTableLoading(true);

      const result = await getDocumentSendPaged.getPaged(
        documentManagementTablePage + 1,
        pageSize,
        statusIdTable,
        searchTerm,
        sortKey,
        sortDirection
      );

      setTotalPending(result.items || []);
      setTotalCount(result.total || 0);
    } catch (error) {
      console.warn('Error fetching documents', error);
      setTotalPending([]);
      setTotalCount(0);
    } finally {
      setTableLoading(false);
    }
  };

  const loadRelatedData = async () => {
    try {
      const related = await getRelatedData();

      const mappedServices = (related.services || [])
        .filter((s) => s.isEnabled)
        .map((s) => ({
          label: s.name,
          value: s.id,
        }));

      const mappedAreas = (related.businessAreas || [])
        .filter((a) => a.isEnabled)
        .map((a) => ({
          label: a.name,
          value: a.id,
        }));

      setServiceOptions(mappedServices);
      setAreaOptions(mappedAreas);
    } catch (e) {
      console.error('Error loading services and areas', e);
    }
  };

  const refreshAllData = async () => {
    setLoading(true);
    await Promise.all([fetchDocumentSendStatsFn(), fetchDocumentSendPagedFn()]);
    setLoading(false);
  };

  const fetchAllDocuments = async () => {
    if (!userId) return [];

    const pageSizeToUse = 1000;
    let page = 1;
    let allItems = [];
    let totalItems = 0;

    try {
      while (true) {
        const result = await getDocumentSendPaged.getPaged(
          page,
          pageSizeToUse,
          statusIdTable,
          searchTerm,
          sortKey,
          sortDirection
        );

        const items = result.items || [];
        const total = result.total || 0;

        if (page === 1) totalItems = total;

        allItems = [...allItems, ...items];

        if (allItems.length >= totalItems) break;

        page++;
      }
    } catch (error) {
      console.error('Error fetching all documents:', error);
    }

    return allItems;
  };

  const buildHumanFilters = () => {
    const filters = {};

    if (searchTerm?.trim()) {
      filters[t('dm_filterSearchBy')] = searchTerm.trim();
    }

    return filters;
  };

  const downloadExcel = async () => {
    try {
      await excelExportLogService.log({
        userId,
        source: t('dm_excelSource'),
        sortKey: sortKey ? (SORT_HUMAN_LABELS[sortKey] ?? sortKey) : null,
        sortDesc: sortDirection === 'desc' ? true : false,
        filters: buildHumanFilters(),
      });
    } catch (error) {
      console.warn(
        'Export log failed, continuing with download.',
        error
      );
    }

    try {
      const allData = await fetchAllDocuments();
      if (!allData.length) return;

      const formatted = allData.map((item) => ({
        [t('dm_excelCol_name')]: item.name,
        [t('dm_excelCol_docType')]: item.documentType,
        [t('dm_excelCol_case')]: item.caseIdentifier,
        [t('dm_excelCol_role')]: item.caseCode,
        [t('dm_excelCol_user')]: item.solicitorName,
        [t('dm_excelCol_date')]: formatDate(item.createdAt),
      }));

      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, t('dm_excelSheet'));

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      saveAs(blob, t('dm_excelFile'));
    } catch (error) {
      console.error('Error generating or downloading Excel:', error);
    }
  };

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchDocumentSendStatsFn();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchDocumentSendPagedFn();
  }, [
    userId,
    documentManagementTablePage,
    pageSize,
    searchTerm,
    sortKey,
    sortDirection,
  ]);

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between'>
        <div>
          <h2 className=' fw-semibold'>{t('dm_pageTitle')}</h2>
          <h4 className=' fw-normal'>
            {t('dm_pageSubtitle')}
          </h4>
        </div>
        <Button
          label={t('dm_newSending')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={async () => {
            await loadRelatedData();
            setShowModalNewSending(true);
          }}
          disabled={!canCreateSubmission}
        />
      </div>
      <div className='row mb-4'>
        <div className='col-12 col-md-6'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dm_statTotalDocs')}
            count={totalPendingStat}
            subtitle={t('dm_statPendingApproval')}
            icon='receipt'
            type='info'
            loading={loading}
          />
        </div>
        <div className='col-12 col-md-6'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dm_statPending')}
            count={totalPendingStat}
            subtitle={t('dm_statRequireAction')}
            icon='clock'
            type='warning'
            loading={loading}
          />
        </div>
      </div>
      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='row'>
          <div className='col-5 d-flex align-items-center'>
            <h3 className='fw-semibold mb-3'>
              {t('dm_tablePendingTitle')}
            </h3>
          </div>
          <div className='col-5'>
            <Input
              icon='search'
              name='searchDocumentSend'
              placeholder={t('dm_searchPlaceholder')}
              type='text'
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className='col-2 d-flex align-items-center'>
            <Button
              iconStart='download'
              label={t('dm_downloadExcel')}
              variant='naked'
              size='sm'
              className='mb-4'
              onClick={downloadExcel}
              disabled={tableLoading || loading || !canDownloadManagementExcel}
            />
          </div>
        </div>
        <div className='table-container table-document-send-request'>
          <Table
            columns={[
              {
                accessor: 'name',
                label: t('dm_col_name'),
                sort: true,
              },
              {
                accessor: 'documentType',
                label: t('dm_col_docType'),
                sort: true,
              },
              {
                accessor: 'caseIdentifier',
                label: t('dm_col_case'),
                sort: true,
                render: (info) => (
                  <div>
                    <p className='caption fw-normal '>{info.caseIdentifier}</p>
                    <p className='mini fw-normal font-light'>
                      {t('dm_col_role', { code: info.caseCode })}
                    </p>
                  </div>
                ),
              },
              {
                accessor: 'solicitorName',
                label: t('dm_col_user'),
                sort: true,
              },
              {
                accessor: 'createdAt',
                label: t('dm_col_date'),
                sort: true,
                render: (info) => formatDate(info.createdAt),
              },
              {
                accessor: 'state',
                label: t('dm_col_actions'),
                render: (info) => (
                  <div className='d-flex'>
                    <IconButton
                      ariaLabel={t('dm_aria_viewDetail')}
                      icon='eye'
                      onClick={() => {
                        setSelectedDocumentId(info.id);
                        setShowModalSendingDetails(true);
                      }}
                      size='md'
                      iconStroke='var(--primary-500)'
                      disabled={!canViewDocument}
                    />
                    <IconButton
                      ariaLabel={t('dm_aria_cancelRequest')}
                      icon='close'
                      onClick={() => {
                        setSelectedDocumentId(info.id);
                        setShowModalCancelRequest(true);
                      }}
                      size='md'
                      iconStroke='var(--error-500)'
                      className='ms-1'
                      disabled={!canCancelRequest}
                    />
                  </div>
                ),
              },
            ]}
            data={totalPending}
            initialPageSize={pageSize}
            page={documentManagementTablePage}
            onPageChange={setDocumentManagementTablePage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setDocumentManagementTablePage(0);
            }}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            paginationMode='server'
            totalItems={totalCount}
            loading={tableLoading}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={({ sortKey: key, direction }) => {
              setSortKey(key);
              setSortDirection(direction);
              setDocumentManagementTablePage(0);
            }}
            moreLines
          />
        </div>
      </Card>
      <ModalNewSending
        DesignSystemComponents={DesignSystemComponents}
        show={showModalNewSending}
        onClose={() => setShowModalNewSending(false)}
        onSuccess={refreshAllData}
        serviceOptions={serviceOptions}
        areaOptions={areaOptions}
        startGlobalLoading={() => setLoading(true)}
        stopGlobalLoading={() => setLoading(false)}
        userId={userId}
      />
      <ModalSendingDetails
        DesignSystemComponents={DesignSystemComponents}
        show={showModalSendingDetails}
        onClose={() => setShowModalSendingDetails(false)}
        documentId={selectedDocumentId}
        onSuccess={refreshAllData}
        startGlobalLoading={() => setLoading(true)}
        stopGlobalLoading={() => setLoading(false)}
        userId={userId}
      />
      <ModalCancelRequest
        DesignSystemComponents={DesignSystemComponents}
        show={showModalCancelRequest}
        onClose={() => setShowModalCancelRequest(false)}
        documentId={selectedDocumentId}
        onSuccess={refreshAllData}
        startGlobalLoading={() => setLoading(true)}
        stopGlobalLoading={() => setLoading(false)}
        userId={userId}
      />
    </div>
  );
};

export default DocumentManagement;
